import { useMemo, useState, type CSSProperties } from 'react';
import {
  Card, Typography, Button, Modal, Form, Select, Checkbox, message, Tag, Tooltip, theme, EmptyState,
  Popconfirm, Spinner, Tree, type TagTone,
} from '../../ui';
import {
  UserPlus, Star, AlertTriangle, ArrowLeftRight,
  Split, Merge, UserCog,
} from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { request } from '../../api/client';
import { usePermission } from '../../store/auth';
import { useAuthStore } from '../../store/auth';
import { TransferTerritoryModal } from './TransferTerritoryModal';
import { CoverageManagerCard } from './CoverageManagerCard';
import { SplitTerritoryModal } from './SplitTerritoryModal';
import { MergeTerritoriesModal } from './MergeTerritoriesModal';
import { SHTransferModal } from './SHTransferModal';
import { CoverageFilters } from './CoverageFilters';

type Level = 'zone' | 'state' | 'district' | 'beat';
const LEVEL_META: Record<Level, { label: string; color: string }> = {
  zone: { label: 'Zone', color: 'purple' },
  state: { label: 'State', color: 'blue' },
  district: { label: 'District', color: 'cyan' },
  beat: { label: 'Beat', color: 'green' },
};
// Which role may be bound at each level (mirrors the backend LEVELS_FOR_ROLE).
// A Sales Head oversees state/district/beat; a Sales Person works district/beat. Zone is
// a navigation container only — nobody is bound at zone level.
const SP_LEVELS: Level[] = ['district', 'beat'];
const SH_LEVELS: Level[] = ['state', 'district', 'beat'];

interface CoverageRow {
  assignmentId: string; territoryId: string; userId: string;
  kind: string; isPrimary: number; fullName: string; roleCode: string;
}

interface AssignTarget { territory: any; assignedUserIds: string[] }

// Find a node by id anywhere in the territory tree.
function findNode(nodes: any[], id?: string): any {
  if (!id) return null;
  for (const n of nodes) { if (String(n.id) === String(id)) return n; const f = findNode(n.children ?? [], id); if (f) return f; }
  return null;
}

/**
 * The ⭐ on a chip: hollow = covers the territory, filled gold = the primary owner (main responsible,
 * gets new customers). If the viewer may assign this role it's clickable: click a hollow star to make
 * them owner (with a confirm when it displaces the current owner), click a filled star to clear it.
 * Ownership is just this annotation + who NEW customers route to — every coverer still sees all customers.
 */
function OwnerStar({ row, canStar, territoryName, otherOwner, onSet }: {
  row: CoverageRow; canStar: boolean; territoryName: string;
  otherOwner?: CoverageRow; onSet: (isPrimary: boolean) => void;
}) {
  const owner = !!row.isPrimary;
  const style: CSSProperties = { display: 'inline-flex', marginInlineEnd: 4, cursor: canStar ? 'pointer' : 'default', opacity: owner || canStar ? 1 : 0.5 };
  const icon = <Star size={15} style={{ verticalAlign: '-2px' }} fill={owner ? '#E7B22C' : 'none'} color={owner ? '#E7B22C' : 'currentColor'} />;
  const stop = (e: any) => e.stopPropagation();
  if (!canStar) {
    return <span style={style} title={owner ? 'Main responsible — owns new customers here' : 'Covers this territory'}>{icon}</span>;
  }
  if (owner) {
    return (
      <Popconfirm placement="top"
        title={`Remove ${row.fullName} as owner of ${territoryName}?`}
        description="New customers here won’t auto-route until you set an owner. Existing customers are untouched."
        okText="Clear owner" onConfirm={() => onSet(false)}>
        <span style={style} title="Owns new customers here — click to clear" onClick={stop}>{icon}</span>
      </Popconfirm>
    );
  }
  if (otherOwner) {
    return (
      <Popconfirm placement="top"
        title={`Make ${row.fullName} the owner of ${territoryName}?`}
        description={`${otherOwner.fullName} keeps their existing customers but stops getting new ones here.`}
        okText="Make owner" onConfirm={() => onSet(true)}>
        <span style={style} title="Click to make main responsible (owns new customers)" onClick={stop}>{icon}</span>
      </Popconfirm>
    );
  }
  // No current owner on this territory → one click, no confirm.
  return (
    <span style={style} title="Click to make main responsible (owns new customers)"
      onClick={(e) => { stop(e); onSet(true); }}>{icon}</span>
  );
}

/** Prune a territory tree to nodes in `domain` (the actor's expanded set), keeping
 *  ancestors of any kept node so the hierarchy path stays visible. */
function keepDomain(nodes: any[], domain: Set<string>): any[] {
  const keep = (n: any): any | null => {
    const kids = (n.children ?? []).map(keep).filter(Boolean);
    if (domain.has(String(n.id)) || kids.length) return { ...n, children: kids };
    return null;
  };
  return nodes.map(keep).filter(Boolean);
}

export function TerritoryCoveragePage({ embedded }: { embedded?: boolean } = {}) {
  const { token } = theme.useToken();
  const canAssignSp = usePermission('TERRITORY.ASSIGN');
  const canAssignSh = usePermission('TERRITORY.ASSIGN.HEAD');
  const canTransfer = usePermission('TERRITORY.TRANSFER');
  const canCoverage = usePermission('TERRITORY.COVERAGE');
  const canSplitMerge = usePermission('TERRITORY.SPLIT_MERGE');
  const qc = useQueryClient();

  const [assignTarget, setAssignTarget] = useState<AssignTarget | null>(null);
  const [transferTarget, setTransferTarget] = useState<{ territory: any; currentPrimary: { userId: string; fullName: string } | null } | null>(null);
  const [splitTarget, setSplitTarget] = useState<any | null>(null);
  const [mergeOpen, setMergeOpen] = useState(false);
  const [shTransferOpen, setShTransferOpen] = useState(false);
  const [unassignedOnly, setUnassignedOnly] = useState(false);
  const [search, setSearch] = useState('');
  const [scopePath, setScopePath] = useState<string[]>();
  const [personId, setPersonId] = useState<string>();

  // Admins see the whole tree; a Sales Head/Person sees only their own domain so the KPIs
  // and "unassigned" tags reflect what they actually manage (coverage chips are already
  // backend-scoped). me/territories returns the actor's expanded set (all active for admins).
  const myRole = useAuthStore((s) => s.user?.role);
  const isAdminView = ['SUPER_ADMIN', 'ADMIN', 'ACCOUNTS'].includes(myRole ?? '');

  const { data: tree, isLoading } = useQuery({
    queryKey: ['territories-tree'],
    queryFn: () => request<any[]>({ url: '/territories/tree' }),
  });
  const { data: coverage } = useQuery({
    queryKey: ['territory-coverage'],
    queryFn: () => request<CoverageRow[]>({ url: '/territory-assignments' }),
  });
  const { data: myTerritories } = useQuery({
    queryKey: ['my-territories'],
    queryFn: () => request<any[]>({ url: '/territory-assignments/me/territories' }),
    enabled: !isAdminView,
  });
  const mySet = useMemo(() => new Set((myTerritories ?? []).map((t: any) => String(t.id))), [myTerritories]);

  // The tree to actually work with — pruned to the actor's domain for non-admins.
  const scopedTree = useMemo(() => (isAdminView ? (tree ?? []) : keepDomain(tree ?? [], mySet)), [tree, isAdminView, mySet]);

  // territoryId → assigned users.
  const byTerritory = useMemo(() => {
    const m = new Map<string, CoverageRow[]>();
    for (const c of coverage ?? []) {
      const k = String(c.territoryId);
      if (!m.has(k)) m.set(k, []);
      m.get(k)!.push(c);
    }
    return m;
  }, [coverage]);

  // A node is "covered" if it OR any ancestor carries a working owner — a Sales Person, or a Sales
  // Head who owns it (is_primary). Computed for EVERY level, so the "Unassigned" filter/tag applies to
  // states and districts too, not only beats. (Zones are containers and are never flagged.)
  const coveredIds = useMemo(() => {
    const spTerr = new Set<string>();
    for (const c of coverage ?? []) if (c.kind !== 'COVERAGE' && (c.roleCode === 'SALES_PERSON' || (c.roleCode === 'SALES_HEAD' && c.isPrimary))) spTerr.add(String(c.territoryId));
    const covered = new Set<string>();
    const walk = (node: any, ancestorCovered: boolean) => {
      const here = ancestorCovered || spTerr.has(String(node.id));
      if (here) covered.add(String(node.id));
      (node.children ?? []).forEach((c: any) => walk(c, here));
    };
    scopedTree.forEach((n) => walk(n, false));
    return covered;
  }, [scopedTree, coverage]);


  const remove = useMutation({
    mutationFn: (assignmentId: string) => request({ url: `/territory-assignments/${assignmentId}`, method: 'DELETE' }),
    onSuccess: () => {
      message.success('Assignment removed');
      qc.invalidateQueries({ queryKey: ['territory-coverage'] });
      qc.invalidateQueries({ queryKey: ['badges'] });
    },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed to remove'),
  });

  // One-click ⭐ owner toggle on a chip.
  const setPrimaryMut = useMutation({
    mutationFn: ({ id, isPrimary }: { id: string; isPrimary: boolean }) =>
      request<any>({ url: `/territory-assignments/${id}/primary`, method: 'POST', data: { isPrimary } }),
    onSuccess: (res: any) => {
      const claimed = res?.claimed ?? 0;
      message.success(res?.isPrimary
        ? (claimed ? `Owner set — ${claimed} unowned customer(s) handed over` : 'Owner set')
        : 'Owner cleared');
      qc.invalidateQueries({ queryKey: ['territory-coverage'] });
      qc.invalidateQueries({ queryKey: ['badges'] });
    },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed to update owner'),
  });

  const canAssignAt = (level: Level) =>
    (canAssignSp && SP_LEVELS.includes(level)) || (canAssignSh && SH_LEVELS.includes(level));

  // Build the antd Tree node, with assignment chips + an "assign" action.
  // Hierarchical Territory picker options (state → district → beat). Starts at States; if more than one
  // zone ever exists, the top zone isn't collapsed, so zones become the first column automatically.
  const terrOptions = useMemo(() => {
    const toOpt = (n: any): any => ({ value: String(n.id), label: n.name, children: n.children?.length ? n.children.map(toOpt) : undefined });
    const start = (scopedTree.length === 1 && scopedTree[0].level === 'zone') ? (scopedTree[0].children ?? []) : scopedTree;
    return start.map(toOpt);
  }, [scopedTree]);
  const personOptions = useMemo(() => {
    const m = new Map<string, { fullName: string; roleCode: string }>();
    for (const c of coverage ?? []) if (!m.has(String(c.userId))) m.set(String(c.userId), { fullName: c.fullName, roleCode: c.roleCode });
    return [...m.entries()].map(([id, u]) => ({ value: id, name: u.fullName, role: (u.roleCode === 'SALES_HEAD' ? 'SH' : 'SP') as 'SH' | 'SP' }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [coverage]);
  // Which subtree to render: the picked state/district/beat (last id in the path), else the whole tree.
  const rootsToRender = useMemo(() => {
    const id = scopePath?.length ? scopePath[scopePath.length - 1] : undefined;
    if (id) { const n = findNode(scopedTree, id); return n ? [n] : []; }
    return scopedTree;
  }, [scopedTree, scopePath]);

  const convert = (t: any): any => {
    const meta = LEVEL_META[t.level as Level];
    const rows = (byTerritory.get(String(t.id)) ?? []).slice().sort((a, b) => (b.isPrimary ? 1 : 0) - (a.isPrimary ? 1 : 0));
    const primarySp = rows.find((r) => r.isPrimary && r.roleCode === 'SALES_PERSON');
    const isUnassigned = t.level !== 'zone' && !coveredIds.has(String(t.id));
    const matchesSearch = !search || `${t.code} ${t.name}`.toLowerCase().includes(search.toLowerCase());
    const matchesPerson = !personId || rows.some((r) => String(r.userId) === personId);
    return {
      key: String(t.id),
      _level: t.level,
      _unassigned: isUnassigned,
      _match: matchesSearch,
      _person: matchesPerson,
      title: (
        <div className="gb-cov-node">
          <span className="gb-cov-main">
            <Tag tone={meta?.color as TagTone} className="gb-cov-pill">{meta?.label ?? t.level}</Tag>
            <b style={{ fontVariantNumeric: 'tabular-nums' }}>{t.code}</b>
            <span className="gb-cov-name">{t.name}</span>
            {isUnassigned && <Tag tone="gold" style={{ marginInlineStart: 4 }}><AlertTriangle size={16} />Unassigned</Tag>}
          </span>
          <span className="gb-cov-chips">
            {rows.map((r) => {
              const roleColor = r.roleCode === 'SALES_HEAD' ? 'blue' : 'green';
              const canStar = (r.roleCode === 'SALES_HEAD' ? canAssignSh : canAssignSp);
              const otherOwner = rows.find((x) => x.isPrimary && String(x.assignmentId) !== String(r.assignmentId));
              return (
                <Tag
                  key={r.assignmentId}
                  tone={roleColor}
                  className="gb-cov-chip"
                >
                  <OwnerStar
                    row={r} canStar={canStar} territoryName={t.name} otherOwner={otherOwner}
                    onSet={(isPrimary) => setPrimaryMut.mutate({ id: r.assignmentId, isPrimary })}
                  />
                  {r.fullName}
                  {canStar && (
                    <Popconfirm
                      title={`Remove ${r.fullName} from ${t.name}?`}
                      okText="Remove" okButtonProps={{ danger: true }}
                      onConfirm={() => remove.mutate(r.assignmentId)}
                    >
                      <span className="gb-cov-x" onClick={(e) => e.stopPropagation()}>×</span>
                    </Popconfirm>
                  )}
                </Tag>
              );
            })}
            {canAssignAt(t.level as Level) && (
              <Tooltip label="Assign a user here">
                <Button
                  variant="default" size="sm" icon={<UserPlus size={16} />}
                  onClick={(e) => { e.stopPropagation(); setAssignTarget({ territory: t, assignedUserIds: rows.map((r: any) => String(r.userId)) }); }}
                >
                  Assign
                </Button>
              </Tooltip>
            )}
            {canTransfer && primarySp && SP_LEVELS.includes(t.level as Level) && (
              <Tooltip label={`Transfer from ${primarySp.fullName} to another SP`}>
                <Button
                  size="sm" icon={<ArrowLeftRight size={16} />}
                  onClick={(e) => { e.stopPropagation(); setTransferTarget({ territory: t, currentPrimary: { userId: String(primarySp.userId), fullName: primarySp.fullName } }); }}
                >
                  Transfer
                </Button>
              </Tooltip>
            )}
            {canSplitMerge && (
              <Tooltip label="Split into a new sibling territory">
                <Button size="sm" icon={<Split size={16} />}
                  onClick={(e) => { e.stopPropagation(); setSplitTarget(t); }}>
                  Split
                </Button>
              </Tooltip>
            )}
          </span>
        </div>
      ),
      children: (t.children ?? []).map(convert),
    };
  };

  // Prune the tree to the active filters, keeping ancestors of any matching node.
  const pruneTree = (nodes: any[]): any[] => {
    const keep = (n: any): any | null => {
      const kids = (n.children ?? []).map(keep).filter(Boolean);
      const self = (!unassignedOnly || n._unassigned) && n._match && n._person;
      if (self || kids.length) return { ...n, children: kids };
      return null;
    };
    return nodes.map(keep).filter(Boolean);
  };
  const treeData = pruneTree(rootsToRender.map(convert));

  return (
    <div>
      {!embedded && <Typography.Title level={4} style={{ marginBottom: 2 }}>Territory Coverage</Typography.Title>}
      <Typography.Paragraph type="secondary" style={{ marginTop: 0 }}>
        Bind Sales Heads to states, districts or beats for oversight and Sales Persons to districts/beats. A beat can hold
        several SPs — the <Star size={14} style={{ color: '#E7B22C', display: 'inline', verticalAlign: '-2px' }} /> primary owns new customers created there.
      </Typography.Paragraph>

      {canCoverage && <CoverageManagerCard />}

      {/* Filters live OUTSIDE the card head — the card head clips (overflow:hidden), which would cut the dropdowns. */}
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <CoverageFilters
          search={search} setSearch={setSearch}
          scopePath={scopePath} setScopePath={setScopePath} terrOptions={terrOptions}
          personId={personId} setPersonId={setPersonId} personOptions={personOptions}
          unassignedOnly={unassignedOnly} setUnassignedOnly={setUnassignedOnly}
        />
        {(canSplitMerge || canAssignSh) && (
          <div className="ml-auto flex gap-2">
            {canSplitMerge && <Button size="sm" icon={<Merge size={16} />} onClick={() => setMergeOpen(true)}>Merge</Button>}
            {canAssignSh && <Button size="sm" icon={<UserCog size={16} />} onClick={() => setShTransferOpen(true)}>Transfer SH</Button>}
          </div>
        )}
      </div>
      <Card>
        {isLoading ? (
          <div className="flex items-center justify-center gap-2 py-10 text-muted"><Spinner /> Loading…</div>
        ) : treeData.length === 0 && !isLoading ? (
          <EmptyState
            title={
              unassignedOnly ? 'No unassigned territories — everything has an owner 🎉'
                : (search || scopePath?.length || personId) ? 'No territories match the current filters.'
                  : 'No territories yet. Create them under Masters → Territories.'} />
        ) : (
          <Tree
            className="gb-cov-tree"
            defaultExpandAll
            showLine={{ showLeafIcon: false }}
            blockNode
            selectable={false}
            treeData={treeData}
          />
        )}
      </Card>

      <AssignModal target={assignTarget} onClose={() => setAssignTarget(null)} />
      <TransferTerritoryModal
        open={!!transferTarget}
        onClose={() => setTransferTarget(null)}
        territory={transferTarget?.territory}
        currentPrimary={transferTarget?.currentPrimary ?? null}
      />
      <SplitTerritoryModal open={!!splitTarget} onClose={() => setSplitTarget(null)} territory={splitTarget} />
      <MergeTerritoriesModal open={mergeOpen} onClose={() => setMergeOpen(false)} />
      <SHTransferModal open={shTransferOpen} onClose={() => setShTransferOpen(false)} />

      <style>{`
        .gb-cov-tree .ant-tree-treenode { padding: 5px 0; align-items: center; width: 100%; }
        .gb-cov-tree .ant-tree-node-content-wrapper { width: 100%; }
        .gb-cov-tree .ant-tree-node-content-wrapper:hover { background: ${token.colorFillTertiary}; }
        .gb-cov-node { display: flex; align-items: center; gap: 10px; padding-right: 8px; flex-wrap: wrap; }
        .gb-cov-main { display: inline-flex; align-items: center; gap: 8px; min-width: 0; }
        .gb-cov-pill { margin: 0; font-size: 10px; line-height: 16px; text-transform: uppercase; letter-spacing: .4px; border: none; }
        .gb-cov-name { color: ${token.colorTextSecondary}; }
        .gb-cov-chips { display: inline-flex; align-items: center; gap: 6px; flex-wrap: wrap; }
        .gb-cov-chip { margin: 0; }
        .gb-cov-x { margin-inline-start: 6px; cursor: pointer; font-weight: 700; opacity: .65; }
        .gb-cov-x:hover { opacity: 1; }
      `}</style>
    </div>
  );
}

function AssignModal({ target, onClose }: { target: AssignTarget | null; onClose: () => void }) {
  const [form] = Form.useForm();
  const qc = useQueryClient();
  const myRole = useAuthStore((s) => s.user?.role);
  const myId = useAuthStore((s) => s.user?.id);

  const open = !!target;
  const t = target?.territory;
  const level: Level | undefined = t?.level;
  const meta = level ? LEVEL_META[level] : null;

  // Role that may be bound at this level. District allows either; the user picks.
  const allowSp = !!level && SP_LEVELS.includes(level);
  const allowSh = !!level && SH_LEVELS.includes(level);
  const role = Form.useWatch('role', form) ?? (allowSp ? 'SALES_PERSON' : 'SALES_HEAD');

  const { data: spUsers } = useQuery({
    queryKey: ['sp-list-all'],
    queryFn: () => request<any>({ url: '/users', params: { role: 'SALES_PERSON', pageSize: 200 } }),
    enabled: open && allowSp,
  });
  const { data: shUsers } = useQuery({
    queryKey: ['sh-list-all'],
    queryFn: () => request<any>({ url: '/users', params: { role: 'SALES_HEAD', pageSize: 200 } }),
    enabled: open && allowSh,
  });

  // A Sales Head may only assign their own reports; Admin/SA see everyone.
  // Exclude deactivated SPs AND anyone already actively bound to THIS territory (assigning them again is a
  // backend no-op — the assign is idempotent — so offering them is just confusing).
  const assignedHere = useMemo(() => new Set((target?.assignedUserIds ?? []).map(String)), [target]);
  const spOptions = useMemo(() => {
    let list: any[] = (spUsers?.data ?? []).filter((u: any) => u.status === 'active' && !assignedHere.has(String(u.id)));
    if (myRole === 'SALES_HEAD') list = list.filter((u) => String(u.reportsTo) === String(myId));
    return list.map((u) => ({ value: String(u.id), label: u.fullName }));
  }, [spUsers, myRole, myId, assignedHere]);
  const shOptions = useMemo(() => (shUsers?.data ?? []).filter((u: any) => u.status === 'active' && !assignedHere.has(String(u.id))).map((u: any) => ({ value: String(u.id), label: u.fullName })), [shUsers, assignedHere]);

  const assign = useMutation({
    mutationFn: (vals: any) => request({
      url: '/territory-assignments', method: 'POST',
      // Both a Sales Person and a Sales Head may own this level; the checkbox is shown for both, so send
      // whatever it holds (only SP/SH are offered here, so no other role can slip through).
      data: { userId: vals.userId, territoryIds: [String(t.id)], isPrimary: (role === 'SALES_PERSON' || role === 'SALES_HEAD') ? !!vals.isPrimary : false },
    }),
    onSuccess: (res: any) => {
      const claimed = res?.claimed ?? 0;
      message.success(claimed ? `Assigned — ${claimed} unowned customer(s) handed over` : 'Assigned');
      qc.invalidateQueries({ queryKey: ['territory-coverage'] });
      qc.invalidateQueries({ queryKey: ['badges'] });
      onClose();
    },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed to assign'),
  });

  return (
    <Modal
      open={open}
      onOpenChange={(o) => { if (!o) onClose(); }}
      title={t ? `Assign to ${meta?.label} · ${t.code}` : 'Assign'}
      footer={<>
        <Button variant="default" onClick={onClose}>Cancel</Button>
        <Button variant="primary" loading={assign.isPending} onClick={() => form.submit()}>Assign</Button>
      </>}
    >
      {t && (
        <Form form={form} layout="vertical" onFinish={(v) => assign.mutate(v)}
          initialValues={{ role: allowSp ? 'SALES_PERSON' : 'SALES_HEAD', isPrimary: allowSp }}>
          <div style={{ marginBottom: 14 }}>
            <Tag tone={meta?.color as TagTone}>{meta?.label}</Tag> <b>{t.code}</b> · {t.name}
          </div>

          {allowSp && allowSh && (
            <Form.Item name="role" label="Bind as">
              <Select
                options={[
                  { value: 'SALES_PERSON', label: 'Sales Person (works this area)' },
                  { value: 'SALES_HEAD', label: 'Sales Head (oversees this area)' },
                ]}
                onChange={() => form.setFieldsValue({ userId: undefined })}
              />
            </Form.Item>
          )}

          <Form.Item name="userId" label={role === 'SALES_HEAD' ? 'Sales Head' : 'Sales Person'}
            rules={[{ required: true, message: 'Pick a user' }]}>
            <Select showSearch optionFilterProp="label"
              placeholder={role === 'SALES_HEAD' ? 'Select a sales head' : 'Select a sales person'}
              notFoundContent={role === 'SALES_PERSON' && myRole === 'SALES_HEAD' ? 'No sales persons in your team' : 'None found'}
              options={role === 'SALES_HEAD' ? shOptions : spOptions} />
          </Form.Item>

          {(role === 'SALES_PERSON' || role === 'SALES_HEAD') && (
            <Form.Item name="isPrimary" valuePropName="checked"
              extra={role === 'SALES_HEAD'
                ? 'As owner, new customers here (and in its beats/districts) route to this Sales Head. Leave off to only oversee this area.'
                : 'The primary owner gets customers newly created in this territory. Leave on unless another owner is already primary here.'}>
              <Checkbox label="Make primary owner" />
            </Form.Item>
          )}
        </Form>
      )}
    </Modal>
  );
}
